import {useCallback} from 'react'

import {isPageParamsDataWithSubIssues} from './query-data-helpers'
import {createParentKeyForSubIssues} from './query-keys'
import {pageParamForInitialPage, type PaginatedMemexItemsQueryVariables} from './types'
import {useMemexItemsPageParams} from './use-memex-items-page-params'

export type UseSubIssuesPageParamsReturnType = {
  setPageParamsForSubIssues: (parentIssueId: string, nwoReference: string) => void
}

/**
 * A hook to set page params on fetching sub-issues of a given parent issue.
 *
 * @param variables - The query variables for the paginated memex items.
 * @returns An object with a method to set sub-issues page params for a given parent issue ID.
 */
export function useSubIssuesPageParams(variables: PaginatedMemexItemsQueryVariables): UseSubIssuesPageParamsReturnType {
  const {getPageParamsQueryData, setPageParamsQueryData} = useMemexItemsPageParams(variables)

  const setPageParamsForSubIssues = useCallback(
    (parentIssueId: string, nwoReference: string) => {
      const prevPageParams = getPageParamsQueryData()
      const prevParentIssues = isPageParamsDataWithSubIssues(prevPageParams) ? prevPageParams.parentIssues : {}
      const parentKey = createParentKeyForSubIssues({parentIssueId, nwoReference})

      // Skip if this parent key already has page params — the data is already cached
      // or being fetched. This avoids unnecessary state updates and re-fetches when
      // restoring expanded state from localStorage.
      if (parentKey in prevParentIssues) return

      setPageParamsQueryData({
        ...prevPageParams,
        parentIssues: {
          ...prevParentIssues,
          [parentKey]: [pageParamForInitialPage],
        },
      })
    },
    [getPageParamsQueryData, setPageParamsQueryData],
  )
  return {setPageParamsForSubIssues}
}
